















import Spinner from '@/components/atoms/Spinner.vue'
import { log } from '@/log'
import { AutoLoginService } from '@ajax/modules/services/auto-login'
import AuthError from '@spa/hl-app/components/error/AuthError.vue'
import { PluginMixin } from '@spa/mixins/plugin'
import { User } from '@spa/store/modules/user/types'
import { Component, Mixins } from 'vue-mixin-decorator'
import { Route } from 'vue-router'

type AuthStatus = 'processing' | 'succeeded' | 'failed'

@Component({
  components: {
    AuthError,
    Spinner,
  },
})
export default class App extends Mixins<PluginMixin>(PluginMixin) {
  authStatus: AuthStatus = 'processing'

  async created() {
    await this.$store.dispatch('view/startLoading')

    /**
     * ホテラバアプリWebViewで開かれるページの認証状態の初期化
     *
     * A. o, hashクエリがついている場合 （アプリWebViewでの最初のページ）
     * 1. o, hash でauto-loginを叩く
     * 2. 1.が失敗すればAuthErrorとする
     * 3. Vuex経由でcurrent_userを取得する
     * 4. 3.でエラーがあればAuthErrorとする
     *
     * B. o, hashクエリがついていない場合（アプリWebViewでページを開い後、SPA内でページ遷移後にリロードした場合など）
     * A-3, 4のみ実行
     *
     * なお、すでにa cookieが存在する場合でもo, hashが存在すれば自動ログインを試みなければならないため、
     * callWithAuthでの自動ログインに任せることはできない（言い換えると、発火する条件が異なる）
     *
     * また、ホテラバアプリUAでの閲覧時には、仮にa cookieが存在しなくても
     * callWithAuthによる自動でのregister_provisionalは発火することはない
     *
     * @see https://my-color.esa.io/posts/1362
     */
    try {
      const { o, hash } = this.$route.query
      const existAutoLoginParams = o && hash

      // ゲストが許可されているページでは、o, hashがなければ処理を終了
      if (this.allowGuest(this.$route) && !existAutoLoginParams) {
        this.authStatus = 'succeeded'
        await this.$store.dispatch('view/stopLoading')

        return
      }

      if (typeof o === 'string' && typeof hash === 'string') {
        // A-1
        const result = await new AutoLoginService().autoLogin(o, hash, 'hl2')
        // A-2
        if (result.isLeft()) {
          throw result.value
        }
      }

      // A-3
      await this.$store.dispatch('user/get')
      // A-4
      if (this.$store.getters['user/hasError'] || !this.user) {
        throw new Error('auto login error')
      }

      if (this.needLogin(this.$route)) {
        throw new Error('should be a registered user')
      }
      this.appendRequireLoginHook()

      this.authStatus = 'succeeded'
    } catch (error) {
      log.error({ error })
      this.authStatus = 'failed'
    } finally {
      await this.$store.dispatch('view/stopLoading')
    }
  }

  get user(): User | undefined {
    return this.$store.state.user.user
  }

  get wrapperClass() {
    return {
      'hlapp-spa': true,
      'l-spa-adjust--loading': this.isLoading,
    }
  }

  get isLoading(): boolean {
    return this.$store.state.view.isLoading
  }

  allowGuest(route: Route): boolean {
    return route.matched.some(r => r.meta.allowGuest)
  }

  needLogin(route: Route): boolean {
    return route.matched.some(r => r.meta.requireLogin)
      && this.user !== undefined
      && this.user.user_status !== 'registered'
  }

  /**
   * SPA内でのページ遷移でrequireLoginなページに到達した場合にAuthErrorにする
   *
   * オートログイン、会員状態（本会員か）の取得中にローディングを表示したかったため、
   * SPAの初期化やbeforeEachでのチェックではなく、App.vueで
   * - オートログイン
   * - 会員状態の取得
   * - 今のページがrequireLoginであるかの判定
   * - 以降のページ遷移のためのrouter hookの登録
   * を行う設計を選択した
   */
  appendRequireLoginHook() {
    this.$router.beforeEach((to, _from, next) => {
      if (this.needLogin(to)) {
        this.authStatus = 'failed'
      } else {
        next()
      }
    })
  }
}
